/** Customer-facing copy for /api/status polling — Gemini Native One-Brain pipeline. */

export interface AnalysisStatusFields {
  status?: string;
  stage?: string;
  progress?: number;
  message?: string;
  error_message?: string;
  failed_step?: string;
  currentStep?: number;
  estimatedTime?: number;
  analysis_engine?: string;
  vision_frames_done?: number;
  vision_frames_total?: number;
  vision_stage_started_at?: string;
  vision_eta_minutes?: number;
}

export interface AnalysisStatusPresentation {
  headline: string;
  detail?: string;
  reassurance?: string;
  staleNotice?: {
    title: string;
    body: string;
  };
  isGeminiProcessing: boolean;
}

export interface AnalysisFailurePresentation {
  title: string;
  message: string;
}

export const STALE_PROGRESS_MS = 3 * 60 * 1000;
export const GEMINI_NATIVE_ESTIMATED_TIME = '2–5 minutes';

export const PROCESSING_REASSURANCE =
  'You can leave this page and return later — we\'ll keep working in the background.';

const UPLOADING_HEADLINE = 'Uploading to Secure Storage';
const UPLOADING_DETAIL =
  'Saving your Twitch video to secure cloud storage before Gemini analysis begins.';

const GEMINI_PROCESSING_HEADLINE = 'Analyzing Game Film (Powered by Gemini)';
const GEMINI_PROCESSING_DETAIL =
  'Gemini Native Vision is reviewing your full broadcast footage. Most reports finish in 2–5 minutes.';

const REPORT_FINALIZATION_HEADLINE = 'Finalizing Scouting Report';
const REPORT_FINALIZATION_DETAIL =
  'Packaging tendencies, scheme insights, and gameplan recommendations. Almost done.';

const STALE_TITLE = 'Still working';
const STALE_BODY =
  'Gemini analysis can pause briefly between stages. You can leave this page and return later.';

const GEMINI_NATIVE_STAGES = new Set([
  'video_received',
  'gemini_upload',
  'gemini_processing',
  'report_finalization',
]);

const LEGACY_GEMINI_PROCESSING_STAGES = new Set(['vision', 'sampling', 'report_generation']);

function normalizedMessage(message?: string): string {
  return (message || '').trim().toLowerCase();
}

export function isGeminiNativeEngine(status: AnalysisStatusFields): boolean {
  return status.analysis_engine === 'gemini_native_video';
}

export function isGeminiProcessingStage(stage?: string): boolean {
  if (!stage) return false;
  return GEMINI_NATIVE_STAGES.has(stage) || LEGACY_GEMINI_PROCESSING_STAGES.has(stage);
}

/** @deprecated Use isGeminiProcessingStage — kept for checklist compatibility during rollout. */
export function isPostSamplingProcessing(status: AnalysisStatusFields): boolean {
  if (isGeminiProcessingStage(status.stage)) {
    return (
      status.stage !== 'video_received' &&
      status.stage !== 'downloading' &&
      status.stage !== 'download_complete' &&
      status.stage !== 'queued'
    );
  }
  const message = normalizedMessage(status.message);
  if (message.includes('gemini native') || message.includes('processing film via gemini')) {
    return true;
  }
  return false;
}

export function statusProgressSignature(status: AnalysisStatusFields): string {
  return `${status.progress ?? 0}::${status.message ?? ''}::${status.stage ?? ''}`;
}

export function isStatusStale(
  unchangedSinceMs: number,
  thresholdMs: number = STALE_PROGRESS_MS
): boolean {
  return unchangedSinceMs >= thresholdMs;
}

export function resolveAnalysisFailureCopy(
  status: AnalysisStatusFields,
  hookError?: string
): AnalysisFailurePresentation | null {
  const terminal =
    status.status === 'failed' ||
    status.status === 'error' ||
    status.stage === 'failed';

  if (!terminal && !hookError) {
    return null;
  }

  const failedStep = (status.failed_step || '').toLowerCase();
  const errorText = (
    status.error_message ||
    status.message ||
    hookError ||
    ''
  ).toLowerCase();

  const combined = `${failedStep} ${errorText}`;

  if (failedStep.includes('gemini_credentials') || combined.includes('gemini_api_key')) {
    return {
      title: 'Gemini configuration error',
      message:
        'Video analysis could not start because Gemini credentials are missing on the worker. Contact support if this persists.',
    };
  }

  if (
    failedStep.includes('download') ||
    combined.includes('download failed') ||
    combined.includes('couldn\'t download') ||
    combined.includes('could not download')
  ) {
    return {
      title: 'Download failed',
      message:
        'We couldn\'t download the video from Twitch. Confirm the link works in your browser and try again.',
    };
  }

  if (
    combined.includes('exceeds maximum length') ||
    combined.includes('vod_duration_exceeds_limit') ||
    (combined.includes('exceeds limit') && combined.includes('min'))
  ) {
    const durationMatch = (status.error_message || status.message || '').match(
      /This upload is ([\d.]+) minutes/i
    );
    const actualDuration = durationMatch?.[1];
    const baseMessage =
      'Scoutlytics currently supports videos up to 120 minutes during closed beta. Try a shorter VOD or clip.';
    return {
      title: 'Video is too long',
      message: actualDuration
        ? `${baseMessage} This upload is ${actualDuration} minutes.`
        : baseMessage,
    };
  }

  if (
    combined.includes('twitch') &&
    (combined.includes('invalid') ||
      combined.includes('unavailable') ||
      combined.includes('not found') ||
      combined.includes('404') ||
      combined.includes('vod'))
  ) {
    return {
      title: 'Video unavailable',
      message:
        'We couldn\'t access this Twitch video. Check that the VOD is public and the URL is correct, then try again.',
    };
  }

  if (
    hookError?.toLowerCase().includes('timeout') ||
    combined.includes('timeout') ||
    combined.includes('timed out') ||
    combined.includes('took too long')
  ) {
    return {
      title: 'Analysis timed out',
      message:
        'This analysis took too long to finish. Try again or use a shorter VOD clip.',
    };
  }

  if (terminal || hookError) {
    return {
      title: 'Analysis couldn\'t complete',
      message:
        status.error_message ||
        status.message ||
        hookError ||
        'Something went wrong during analysis. Try a different VOD or contact support with your report ID.',
    };
  }

  return null;
}

export function resolveAnalysisStatusCopy(
  status: AnalysisStatusFields,
  options?: {
    staleMs?: number;
  }
): AnalysisStatusPresentation {
  const staleMs = options?.staleMs ?? 0;
  const reassurance = PROCESSING_REASSURANCE;
  const isGeminiProcessing = isGeminiProcessingStage(status.stage);

  const staleNotice =
    staleMs >= STALE_PROGRESS_MS
      ? { title: STALE_TITLE, body: STALE_BODY }
      : undefined;

  if (
    status.stage === 'report_finalization' ||
    status.stage === 'report_generation'
  ) {
    return {
      headline: REPORT_FINALIZATION_HEADLINE,
      detail: REPORT_FINALIZATION_DETAIL,
      reassurance,
      staleNotice,
      isGeminiProcessing: true,
    };
  }

  if (
    status.stage === 'gemini_upload' ||
    status.stage === 'gemini_processing' ||
    status.stage === 'vision' ||
    status.stage === 'sampling'
  ) {
    return {
      headline: GEMINI_PROCESSING_HEADLINE,
      detail: GEMINI_PROCESSING_DETAIL,
      reassurance,
      staleNotice,
      isGeminiProcessing: true,
    };
  }

  if (status.stage === 'video_received' || status.stage === 'job_starting') {
    return {
      headline: GEMINI_PROCESSING_HEADLINE,
      detail: 'Preparing your film for Gemini Native Vision analysis.',
      reassurance,
      staleNotice,
      isGeminiProcessing: true,
    };
  }

  if (status.stage === 'downloading' || status.stage === 'download_complete') {
    return {
      headline: UPLOADING_HEADLINE,
      detail: status.message || UPLOADING_DETAIL,
      reassurance,
      staleNotice,
      isGeminiProcessing: false,
    };
  }

  if (status.stage === 'queued' || status.status === 'queued') {
    return {
      headline: 'Queued for analysis',
      detail: status.message || 'Your video is in line to be processed.',
      reassurance,
      staleNotice,
      isGeminiProcessing: false,
    };
  }

  if (isGeminiProcessing) {
    return {
      headline: GEMINI_PROCESSING_HEADLINE,
      detail: GEMINI_PROCESSING_DETAIL,
      reassurance,
      staleNotice,
      isGeminiProcessing: true,
    };
  }

  return {
    headline: 'Analysis in progress',
    detail: status.message || 'Processing your video.',
    reassurance,
    staleNotice,
    isGeminiProcessing: false,
  };
}
